import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, Sprout, Home, ClipboardList, PenLine, Inbox, HandHeart, Target, ChevronRight, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import HomeTab from '../components/teamdev/HomeTab';
import CheckinTab from '../components/teamdev/CheckinTab';
import FeedbackForm from '../components/teamdev/FeedbackForm';
import InboxTab from '../components/teamdev/InboxTab';
import RequestsTab from '../components/teamdev/RequestsTab';
import GoalsTab from '../components/teamdev/GoalsTab';
import MonthlyTab from '../components/teamdev/MonthlyTab';
import '../components/teamdev/teamdev.css';

const LEITGEDANKE = 'Hier will dir niemand etwas Böses. Wir helfen einander, besser zu werden — das ist alles.';

const ONBOARDING = [
  { emoji: '💛', title: 'Hier will dir niemand etwas Böses.',
    text: 'Dieses Modul hat genau einen Zweck: dass wir einander helfen, besser zu werden. Kein Bewerten, kein Vergleichen, kein Hintenrum — nur Menschen, die es gut miteinander meinen.' },
  { emoji: '✍️', title: 'Hier gibt’s keine Noten.',
    text: 'Kein Rating, kein Ranking, keine Anonymität. Alles, was du schreibst, steht mit deinem Namen dahinter — deshalb schreiben wir so, wie wir es auch sagen würden.' },
  { emoji: '🔍', title: 'Gutes Feedback = konkret + beobachtbar.',
    text: '‚Du hast im Call dreimal unterbrochen‘ kann man ändern. ‚Du bist ungeduldig‘ nicht. Das System hilft dir mit der Situation-Verhalten-Wirkung-Struktur.' },
  { emoji: '🌱', title: 'Deine Ziele, deine Regeln.',
    text: 'Du formulierst deine Entwicklungsziele selbst — als Ambition, nicht als Schwäche. Andere feuern dich mit 📈 an, geben 💡 Ideen oder teilen Beobachtungen. Was du damit machst, entscheidest du.' },
  { emoji: '🙋', title: 'Der beste Move: selbst fragen.',
    text: 'Erbetenes Feedback ist wertvoller als ungefragtes. Wenn du wissen willst, wie etwas ankam — fordere Feedback an, mit einer konkreten Frage.' },
];

function Onboarding({ onDone }) {
  const [step, setStep] = useState(0);
  const s = ONBOARDING[step];
  const last = step === ONBOARDING.length - 1;
  return (
    <div className="td-rise bg-card border border-border rounded-lg p-10 max-w-xl mx-auto text-center" data-testid="td-onboarding">
      <p className="text-5xl mb-5 td-pop" key={step}>{s.emoji}</p>
      <h2 className="font-heading text-lg text-foreground td-pop" key={`t${step}`}>{s.title}</h2>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-md mx-auto">{s.text}</p>
      <div className="flex items-center justify-center gap-1.5 mt-7">
        {ONBOARDING.map((_, i) => (
          <button key={i} onClick={() => setStep(i)}
            className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-emerald-400' : 'w-1.5 bg-border hover:bg-muted-foreground/40'}`} />
        ))}
      </div>
      <Button onClick={() => (last ? onDone() : setStep(step + 1))} data-testid="td-onboarding-next"
        className="mt-7 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-7">
        {last ? 'Los geht’s' : 'Weiter'} <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
      {!last && (
        <p className="mt-3">
          <button onClick={onDone} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors" data-testid="td-onboarding-skip">
            Überspringen
          </button>
        </p>
      )}
    </div>
  );
}

const TABS = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'checkin', label: 'Check-in', icon: ClipboardList },
  { id: 'give', label: 'Feedback geben', icon: PenLine },
  { id: 'inbox', label: 'Postfach', icon: Inbox },
  { id: 'requests', label: 'Anfragen', icon: HandHeart },
  { id: 'goals', label: 'Ziele', icon: Target },
  { id: 'monthly', label: 'Monats-Runde', icon: Handshake },
];

export default function TeamDevelopmentPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('home');
  const [prefill, setPrefill] = useState(null);

  const load = useCallback(() => {
    api.teamDevOverview().then((res) => setData(res.data)).catch(() => toast.error('Seite konnte nicht geladen werden')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const finishOnboarding = async () => {
    try { await api.teamDevOnboarded(); setData((d) => ({ ...d, me: { ...d.me, onboarded: true } })); }
    catch { toast.error('Das hat nicht geklappt'); }
  };

  const gotoTab = (t, pf = null) => { setPrefill(pf); setTab(t); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const answerRequest = (r) => gotoTab('give', { kind: 'request', request: r });
  const gotoObservation = (goal) => gotoTab('give', { kind: 'goal', goal });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const counts = data?.counts || {};
  const badge = (id) => {
    if (id === 'inbox') return (counts.unread || 0) + (counts.ideas_to_react || 0);
    if (id === 'requests') return counts.requests_for_me || 0;
    return 0;
  };

  return (
    <div className="space-y-6 max-w-5xl pb-8" data-testid="team-development-page">
      <div>
        <h1 className="font-heading text-2xl text-foreground tracking-tight flex items-center gap-2">
          <Sprout className="w-5 h-5 text-emerald-400" strokeWidth={1.5} /> Team Development
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Wir helfen einander, besser zu werden — ohne Noten, ohne Anonymität.</p>
      </div>

      {!data?.me?.onboarded ? (
        <Onboarding onDone={finishOnboarding} />
      ) : (
        <>
          <div className="flex flex-wrap gap-1 border-b border-border pb-2 sticky top-0 bg-background z-10">
            {TABS.map((t) => {
              const b = badge(t.id);
              const dot = t.id === 'checkin' && !counts.checkin_done;
              return (
                <button key={t.id} onClick={() => gotoTab(t.id)} data-testid={`td-tab-${t.id}`}
                  className={`relative flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border transition-colors ${
                    tab === t.id ? 'bg-secondary text-foreground border-border' : 'bg-transparent text-muted-foreground border-transparent hover:bg-secondary/50 hover:text-foreground'}`}>
                  <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t.label}
                  {b > 0 && (
                    <span className="bg-emerald-500 text-white text-[9px] font-bold font-mono rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">{b}</span>
                  )}
                  {dot && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Check-in noch offen" data-testid="td-checkin-dot" />}
                </button>
              );
            })}
          </div>

          {tab === 'home' && <HomeTab gotoTab={gotoTab} />}
          {tab === 'checkin' && <CheckinTab gotoTab={gotoTab} onSaved={load} />}
          {tab === 'give' && (
            <FeedbackForm members={data?.members || []} meId={data?.me?.id} prefill={prefill}
              clearPrefill={() => setPrefill(null)} onSent={() => { load(); gotoTab('home'); }} />
          )}
          {tab === 'inbox' && <InboxTab meId={data?.me?.id} members={data?.members || []} refreshCounts={load} gotoTab={gotoTab} />}
          {tab === 'requests' && <RequestsTab members={data?.members || []} meId={data?.me?.id} onAnswer={answerRequest} refreshCounts={load} />}
          {tab === 'goals' && <GoalsTab meId={data?.me?.id} gotoObservation={gotoObservation} />}
          {tab === 'monthly' && <MonthlyTab meId={data?.me?.id} />}

          <p className="text-center text-[11px] text-muted-foreground/70 italic pt-4 border-t border-border/40" data-testid="td-footer">
            {LEITGEDANKE}
          </p>
        </>
      )}
    </div>
  );
}
